import type { Module } from 'vuex';
import { opportunityModule } from './opportunity';
import { pipelineModule } from './pipeline';
import { dashboardModule } from './dashboard';
import { allActivitiesModule } from './allActivities';
import { opportunityDetailModule } from './opportunityDetail';
import { configurationCrmModule } from './configurationCrm';
import { advancedConfigModule } from './advancedConfig';
import { crmAccessModule } from './crmAccess';
import type { CrmState } from './types';

/**
 * `opportunity.ts` and `pipeline.ts` are merged into a single namespaced `crm` module rather than
 * nested as child modules, so store actions/getters are addressed as `crm/fetchOpportunities`,
 * `crm/stages`, etc. — one flat namespace, matching the Phase 1 spec.
 *
 * Everything added in Phase 2 is registered as a **nested** namespaced child instead, one per sidebar
 * entry (`crm/dashboard/…`, `crm/allActivities/…`, `crm/crmAccess/…`, `crm/configurationCrm/…`,
 * `crm/advancedConfig/…`),
 * plus `crm/opportunityDetail/…` for the opportunity page.
 * Flattening them the same way would collide: every one of them wants `loading`, `error` and a
 * `fetch` action. Nesting also gives each page a file it owns outright — this index is shared and
 * should not need touching again (docs/CRM_SECTION_CONTRACT.md §6).
 */
const crmModule: Module<CrmState, unknown> = {
    namespaced: true,
    // Vuex merges each nested module's state into `state.crm` when the store is created, but its types
    // don't model that composition — a parent `state()` is required to produce the whole `S`. The cast
    // narrows to the half this function actually owns; the nested keys are filled in at runtime.
    state: () => ({
        ...opportunityModule.state(),
        ...pipelineModule.state(),
    }) as CrmState,
    mutations: {
        ...opportunityModule.mutations,
        ...pipelineModule.mutations,
    },
    actions: {
        ...opportunityModule.actions,
        ...pipelineModule.actions,
    },
    getters: {
        ...opportunityModule.getters,
        ...pipelineModule.getters,
    },
    modules: {
        dashboard: dashboardModule,
        allActivities: allActivitiesModule,
        opportunityDetail: opportunityDetailModule,
        configurationCrm: configurationCrmModule,
        advancedConfig: advancedConfigModule,
        crmAccess: crmAccessModule,
    },
};

export const crmStoreModules = { crm: crmModule };

export type { CrmState } from './types';
